'use client';

import Button from '@basic/Button';
import GraphicWithText from '@basic/GraphicWithText';
import GoalEmptyStateAmbience from '@components/animations/GoalEmptyStateAmbience';
import GoalMountainGrapic from '@components/graphics/GoalMountainGrapic';
import ArrowRightIcon from '@components/icons/ArrowRightIcon';
import classNames from 'classnames';
import { useInView } from 'framer-motion';
import { ReactNode, useEffect, useRef, useState } from 'react';

const WAVE_EMOJI = '👋';

// Wrap a waving-hand emoji in the header in an animated span so it can wave on its own
// (the rest of the header stays static). Returns the header unchanged when there's no hand.
function withWavingHand(header: string): ReactNode {
  if (!header.includes(WAVE_EMOJI)) return header;
  const [before, ...rest] = header.split(WAVE_EMOJI);
  return (
    <>
      {before}
      <span className='goal-wave' role='img' aria-label='waving hand'>
        {WAVE_EMOJI}
      </span>
      {rest.join(WAVE_EMOJI)}
    </>
  );
}

interface NoSubscriptionsComponentProps extends React.HTMLAttributes<HTMLDivElement> {
  header: string;
  text?: string;
  buttonText: string;
  headerVariant?: 'primary' | 'secondary';
  buttonOnClick: () => void;
  animated?: boolean;
}

const NoSubscriptionsComponent = ({
  header,
  text,
  headerVariant,
  buttonText,
  buttonOnClick,
  animated = false,
  ...props
}: NoSubscriptionsComponentProps) => {
  const ref = useRef<HTMLDivElement>(null);
  // once:false is intentional — `visible` also drives the active/pause gate (idle float + sheen)
  const visible = useInView(ref);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (animated && visible) setEntered(true);
  }, [animated, visible]);

  const headerNode = animated ? withWavingHand(header) : header;

  const graphic = animated ? (
    <GoalMountainGrapic animated withSheen entered={entered} active={visible} />
  ) : (
    <GoalMountainGrapic />
  );

  const content = (
    <>
      <GraphicWithText
        graphic={graphic}
        header={headerNode}
        headerVariant={headerVariant}
        text={text}
      />
      <Button className='sm:w-[370px]' type='primary' size='large' onClick={buttonOnClick}>
        {buttonText}
        <ArrowRightIcon size={24} />
      </Button>
    </>
  );

  return (
    <div
      {...props}
      ref={ref}
      className={classNames(
        'flex flex-col justify-center items-center sm:h-3/4 h-full w-full pt-sm sm:pt-none gap-huge',
        animated && 'relative'
      )}
    >
      {animated ? (
        <>
          <GoalEmptyStateAmbience active={visible} />
          <div className='relative z-10 flex flex-col items-center gap-huge'>{content}</div>
        </>
      ) : (
        content
      )}
    </div>
  );
};

export default NoSubscriptionsComponent;
